// Helpers for the per-time-range-template task lock:
// lock.editableTimeRangeTemplateIds lists the timeRangeTemplate ids whose time
// ranges may be edited. An empty array locks every time range. The legacy
// lock.timeRangesEditable boolean is ignored by the API and the task viewer —
// the API resolves legacy locks (no ids array) to the ids of the task's own
// time range rows on read, i.e. fully editable.

export function isTimeRangeEditable(lock, timeRangeTemplateId) {
  const ids = lock?.editableTimeRangeTemplateIds;
  if (!Array.isArray(ids)) {
    return true;
  }
  return ids.includes(timeRangeTemplateId);
}

export function buildNewTaskLock(editableTimeRangeTemplateIds) {
  return {
    subtasksEditable: true,
    structureEditable: true,
    editableTimeRangeTemplateIds: [...new Set(editableTimeRangeTemplateIds || [])].filter(Boolean),
  };
}

// Copy an existing task's lock for a commit payload. A lock without the ids
// array would be migrated by the commit to whatever the client sends (missing
// means "lock everything"), so resolve it the same way the API does on read:
// expose the task's own time range rows.
export function echoExistingLock(existingTask) {
  const lock = { ...(existingTask?.lock || {}) };
  if (!Array.isArray(lock.editableTimeRangeTemplateIds)) {
    lock.editableTimeRangeTemplateIds = [...new Set(
      (existingTask?.timeRanges || []).map((row) => row.timeRangeTemplateId).filter(Boolean),
    )];
  }
  return lock;
}

// Per-template editability across a nested task tree:
// [{ timeRangeTemplateId, title, state }] with state 'editable' | 'locked' |
// 'mixed'. An empty tree reports every template as 'editable'.
export function deriveTimeRangeEditability(tasks, timeRangeTemplates) {
  const stats = new Map((timeRangeTemplates || []).map((template) => [template.id, { editable: 0, locked: 0 }]));
  const walk = (list) => (list || []).forEach((task) => {
    stats.forEach((stat, templateId) => {
      if (isTimeRangeEditable(task.lock, templateId)) {
        stat.editable += 1;
      } else {
        stat.locked += 1;
      }
    });
    walk(task.children);
  });
  walk(tasks);
  return (timeRangeTemplates || []).map((template) => {
    const { editable, locked } = stats.get(template.id);
    return {
      timeRangeTemplateId: template.id,
      title: template.title || template.id,
      state: editable && locked ? 'mixed' : (locked ? 'locked' : 'editable'),
    };
  });
}

export function indexTasksById(tasks, map = new Map()) {
  (tasks || []).forEach((task) => {
    map.set(task.id, task);
    indexTasksById(task.children, map);
  });
  return map;
}

// Stamp locks onto a processed nested tree before commit: existing tasks echo
// their live lock (the commit keeps or migrates it server-side), new tasks get
// an open lock with the given editable template ids.
export function applyTaskLocks(nestedTasks, existingTasksById, editableTimeRangeTemplateIds) {
  const walk = (list) => (list || []).forEach((task) => {
    const existingTask = existingTasksById?.get(task.id);
    task.lock = existingTask
      ? echoExistingLock(existingTask)
      : buildNewTaskLock(editableTimeRangeTemplateIds);
    walk(task.children);
  });
  walk(nestedTasks);
  return nestedTasks;
}

// Stamp a lock key onto every task of a processed nested tree before commit,
// replacing whatever key processing produced (the P6 default, or an existing
// task's echoed key). No-op without a key, so the processed keys stand.
export function applyTaskKey(nestedTasks, key) {
  if (!key) return nestedTasks;
  const walk = (list) => (list || []).forEach((task) => {
    task.key = key;
    walk(task.children);
  });
  walk(nestedTasks);
  return nestedTasks;
}
